(() => {
  const root = document.documentElement;
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const routes = window.themeRoutes || {};
  const drawerFocus = new WeakMap();

  /* Reported as "page opens scrolled to the bottom" in Safari. Safari
     restores the previous scroll offset more aggressively than other
     browsers on reload/back-forward navigation, and that saved offset can
     land somewhere on the page that no longer matches once async content
     (hero video, web fonts) finishes loading and reflows the layout above
     it. Forcing manual restoration on a real fresh load (not a same-page
     hash link, which should still jump to its target) removes the browser's
     own guess entirely. */
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  const isHomePage = () => document.body?.classList.contains('template-home');
  const shouldForceTop = () => isHomePage() && !window.location.hash;
  let canForceTop = shouldForceTop();
  let forceTopFrames = 0;
  const cancelForceTop = () => { canForceTop = false; };
  const forceTop = () => {
    if (!canForceTop || !shouldForceTop()) return;
    window.scrollTo(0, 0);
  };
  const forceTopEarly = () => {
    if (!canForceTop || !shouldForceTop()) return;
    if (window.scrollY > 8) {
      cancelForceTop();
      return;
    }
    forceTop();
    forceTopFrames += 1;
    if (forceTopFrames < 3) requestAnimationFrame(forceTopEarly);
  };
  ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((eventName) => {
    window.addEventListener(eventName, cancelForceTop, { passive: true, once: true });
  });
  if (canForceTop) {
    forceTop();
    requestAnimationFrame(forceTopEarly);
    window.addEventListener('pageshow', () => {
      canForceTop = shouldForceTop() && window.scrollY <= 8;
      forceTopFrames = 0;
      forceTopEarly();
    });
  }

  const announce = (message) => {
    const status = document.getElementById('a11y-status');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  const openDrawer = (drawer, opener = document.activeElement) => {
    if (!drawer) return;
    document.querySelectorAll('.drawer.is-open').forEach((open) => {
      if (open === drawer) return;
      closeDrawer(open, false);
    });
    drawerFocus.set(drawer, opener);
    drawer.setAttribute('open', '');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    document.querySelectorAll(`[data-open-drawer="${drawer.id}"]`).forEach((opener) => opener.setAttribute('aria-expanded', 'true'));
    drawer.querySelector(focusable)?.focus();
  };

  const closeDrawer = (drawer, restoreFocus = true) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.removeAttribute('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[data-open-drawer="${drawer.id}"]`).forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('is-locked');
    if (restoreFocus) drawerFocus.get(drawer)?.focus?.();
    drawerFocus.delete(drawer);
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(focusable)].filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    if (event.shiftKey && document.activeElement === items[0]) {
      event.preventDefault(); items.at(-1).focus();
    } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
      event.preventDefault(); items[0].focus();
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-drawer]');
    if (opener) {
      event.preventDefault();
      const target = document.getElementById(opener.dataset.openDrawer);
      if (target && target.classList.contains('is-open')) {
        closeDrawer(target);
      } else {
        openDrawer(target, opener);
      }
    }
    const closer = event.target.closest('[data-close-drawer]');
    if (closer) closeDrawer(closer.closest('.drawer'));
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const input = quantityButton.parentElement.querySelector('input[type="number"]');
      const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityButton.dataset.quantityChange));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      updateCartLine(cartChange.dataset.cartChange, Number(cartChange.dataset.quantity), cartChange);
    }
    const giftWrapToggle = event.target.closest('[data-cart-gift-wrap-toggle]');
    if (giftWrapToggle) {
      updateGiftWrap(giftWrapToggle);
    }
  });

  let cartNoteTimer;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(cartNoteTimer);
    cartNoteTimer = setTimeout(() => updateCartNote(note.value), 600);
  });

  document.addEventListener('keydown', (event) => {
    const drawer = document.querySelector('.drawer.is-open');
    if (!drawer) return;
    if (event.key === 'Escape') closeDrawer(drawer);
    trapFocus(event, drawer);
  });

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', (event) => this.submit(event));
    }
    async submit(event) {
      if (!this.form || this.form.dataset.native === 'true') return;
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      const original = button?.textContent;
      const errors = this.querySelector('[data-product-errors]');
      if (errors) { errors.hidden = true; errors.textContent = ''; }
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      try {
        const response = await fetch(routes.cartAdd || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
        await refreshCart();
        announce(`${data.product_title || 'Item'} was added to your cart.`);
        const drawer = document.getElementById('CartDrawer');
        if (drawer) openDrawer(drawer);
      } catch (error) {
        if (errors) { errors.hidden = false; errors.textContent = error.message; }
        announce(error.message);
      } finally {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = original; }
      }
    }
  }

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.variants = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
      this.addEventListener('change', () => this.update());
    }
    update() {
      const options = [...this.querySelectorAll('fieldset')].map((group) => group.querySelector('input:checked')?.value);
      const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
      const section = this.closest('[data-product-section]');
      /* Scoped to the main buy-buttons form's hidden id input specifically —
         a plain `form [name="id"]` selector also matches the discovery-upsell
         block's own separate form/hidden id (which renders earlier in the
         DOM), silently overwriting the sample product's variant id with
         whatever size the shopper picks for the main product. */
      const input = section?.querySelector('[data-main-variant-id]');
      const button = section?.querySelector('[data-add-to-cart]');
      const buyNow = section?.querySelector('.pdp-buy-now-wrap');
      const stickyButton = section?.parentElement?.querySelector('[data-sticky-atc-submit]');
      const availability = section?.querySelector('[data-variant-availability]');
      if (!variant) {
        if (input) input.value = '';
        if (button) { button.disabled = true; button.textContent = button.dataset.unavailable; }
        if (stickyButton) { stickyButton.disabled = true; stickyButton.textContent = button?.dataset.unavailable || 'Unavailable'; }
        if (availability) availability.textContent = availability.dataset.unavailable || 'Currently unavailable';
        if (buyNow) buyNow.hidden = true;
        return;
      }
      if (input) input.value = variant.id;
      if (buyNow) buyNow.hidden = !variant.available;
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;
      }
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = variant.available ? (button?.dataset.add || 'Add to Bag') : (button?.dataset.soldOut || 'Sold Out');
      }
      if (availability) {
        availability.textContent = variant.available ? availability.dataset.available : availability.dataset.unavailable;
      }
      /* The sticky add-to-cart bar (.sticky-atc) is a sibling of [data-product-section],
         not a descendant, so it needs the shared parent to be reached too. */
      const priceScope = section?.parentElement || section;
      priceScope?.querySelectorAll('[data-price]').forEach((price) => {
        price.innerHTML = variant.compare_at_price > variant.price
          ? `<s>${formatMoney(variant.compare_at_price)}</s> <span class="price__sale">${formatMoney(variant.price)}</span>`
          : formatMoney(variant.price);
      });
      section?.querySelectorAll('[data-variant-title]').forEach((title) => { title.textContent = variant.title; });
      const sku = section?.querySelector('[data-sku]');
      if (sku) { sku.textContent = variant.sku || ''; sku.closest('p').hidden = !variant.sku; }
      const gallery = section?.querySelector('product-gallery');
      const mediaId = variant.featured_media?.id || gallery?.dataset.initialMediaId;
      if (mediaId) gallery?.querySelector(`[data-media-id="${mediaId}"]`)?.click();
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      /* Shared by both thumbnail clicks and the swipe gesture below, so a
         video always autoplays the moment its panel becomes active and
         pauses (reset to the start) the moment it's swiped/clicked away
         from — regardless of which interaction switched it. */
      const showMedia = (mediaId) => {
        this.querySelectorAll('[data-media-id]').forEach((item) => item.setAttribute('aria-current', item.dataset.mediaId === mediaId ? 'true' : 'false'));
        this.querySelectorAll('[data-product-media]').forEach((panel) => {
          const isActive = panel.dataset.productMedia === mediaId;
          panel.hidden = !isActive;
          const video = panel.querySelector('video');
          if (!video) return;
          if (isActive) {
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      };

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-id]');
        if (!thumb) return;
        showMedia(thumb.dataset.mediaId);
      });

      /* Swipe left/right on the main image to move to the next/previous
         media — the gallery previously only supported switching media by
         tapping a thumbnail. */
      const main = this.querySelector('.gallery-main');
      if (main) {
        let startX = 0;
        let startY = 0;
        let tracking = false;
        main.addEventListener('touchstart', (event) => {
          const touch = event.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          tracking = true;
        }, { passive: true });
        main.addEventListener('touchend', (event) => {
          if (!tracking) return;
          tracking = false;
          const touch = event.changedTouches[0];
          const deltaX = touch.clientX - startX;
          const deltaY = touch.clientY - startY;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          const thumbs = [...this.querySelectorAll('[data-media-id]')];
          if (thumbs.length < 2) return;
          const currentIndex = thumbs.findIndex((item) => item.getAttribute('aria-current') === 'true');
          if (currentIndex === -1) return;
          const nextIndex = deltaX < 0
            ? Math.min(thumbs.length - 1, currentIndex + 1)
            : Math.max(0, currentIndex - 1);
          if (nextIndex !== currentIndex) showMedia(thumbs[nextIndex].dataset.mediaId);
        }, { passive: true });
      }
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 250);
      });
    }
    async search() {
      const term = this.input.value.trim();
      this.controller?.abort();
      if (term.length < 2) { this.results.hidden = true; return; }
      this.controller = new AbortController();
      const endpoint = routes.predictiveSearch || `${window.Shopify?.routes?.root || '/'}search/suggest`;
      const url = `${endpoint}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,article,page&resources[limit]=6`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Predictive search request failed.');
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = html?.innerHTML || '';
        this.results.hidden = !this.results.innerHTML.trim();
      } catch (error) {
        if (error.name !== 'AbortError') this.results.hidden = true;
      }
    }
  }

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: window.themeCurrency || 'INR' }).format(cents / 100);
  };

  const refreshCart = async () => {
    const cartWasOpen = document.getElementById('CartDrawer')?.classList.contains('is-open');
    const rootUrl = window.Shopify?.routes?.root || '/';
    const ids = ['cart-drawer', 'header'];
    /* The full /cart page's own section (main-cart.liquid) isn't part of the
       header group, so it renders under a template-scoped id (e.g.
       "template--123__main") rather than a short well-known name. Detect it
       from the DOM so line-item quantity/remove changes made on /cart itself
       re-render that section too, not just the drawer and header cart count. */
    const cartPageId = document.querySelector('.cart-page')?.closest('[id^="shopify-section"]')?.id.replace(/^shopify-section-/, '');
    if (cartPageId) ids.push(cartPageId);
    const response = await fetch(`${rootUrl}?sections=${ids.join(',')}`);
    const sections = await response.json();
    /* Sections rendered inside a section group (header-group.json) get IDs
       like "shopify-section-sections--<id>__cart-drawer", not the short
       "shopify-section-cart-drawer" this used to look for — that mismatch
       meant this replacement silently never ran; only a full page reload
       ever showed a fresh cart. Match by suffix instead of exact id. */
    ids.forEach((id) => {
      const current = document.querySelector(`[id^="shopify-section"][id$="__${id}"], #shopify-section-${id}`);
      if (!current || !sections[id]) return;
      const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
      const replacement = parsed.querySelector(`[id^="shopify-section"][id$="__${id}"], #shopify-section-${id}`);
      if (replacement) current.replaceWith(replacement);
    });
    initHeaderScroll();
    syncCartCoupons();
    if (cartWasOpen) openDrawer(document.getElementById('CartDrawer'));
  };

  /* Cart coupon rows (snippets/cart-milestones.liquid).

     Applied state cannot be rendered by Liquid: the Liquid cart object has no
     discount_codes property, and cart_level_discount_applications stays empty
     for code-based discounts (it only fills for automatic ones) — both verified
     against the live store. The Ajax Cart API does expose discount_codes with an
     `applicable` flag, so the rows are rendered neutral and reconciled here.

     Re-queried on every call rather than bound per element, so it keeps working
     after refreshCart() swaps the drawer/cart section HTML. */
  const syncCartCoupons = async () => {
    const rows = document.querySelectorAll('[data-coupon-code]');
    if (!rows.length) return;
    let cart;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      return;
    }
    /* Only codes Shopify itself reports as applicable count as applied — a code
       can sit on the cart with applicable:false when its conditions aren't met,
       and showing that as "Applied" would promise a discount checkout won't give. */
    const active = (cart.discount_codes || [])
      .filter((entry) => entry.applicable)
      .map((entry) => String(entry.code).toUpperCase());
    const saved = cart.total_discount || 0;

    rows.forEach((row) => {
      const code = String(row.dataset.couponCode || '').toUpperCase();
      const isApplied = active.includes(code);
      const button = row.querySelector('[data-apply-coupon]');
      const flag = row.querySelector('.cart-coupon__applied-flag');
      const offer = row.querySelector('.cart-coupon__desc-offer');
      const appliedText = row.querySelector('.cart-coupon__desc-applied');
      row.classList.toggle('is-applied', isApplied);
      if (button) button.hidden = isApplied;
      if (flag) flag.hidden = !isApplied;
      if (offer) offer.hidden = isApplied;
      if (appliedText) {
        appliedText.hidden = !isApplied;
        /* The figure comes straight from Shopify's own total_discount, never a
           percentage recomputed here, so the cart can't disagree with checkout. */
        appliedText.textContent = isApplied && saved > 0 ? `You save ${formatMoney(saved)}` : '';
      }
    });
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-apply-coupon]');
    if (!button) return;
    const code = button.dataset.applyCoupon;
    if (!code || button.disabled) return;
    const original = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      /* Shopify has no Ajax endpoint for applying a discount code; /discount/<code>
         is the supported route and associates the code with the current cart before
         redirecting. Fetching it runs that same server-side association without
         navigating the shopper away from the cart. */
      await fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      await refreshCart();
      announce(`Discount ${code} applied.`);
    } catch (error) {
      announce('That discount could not be applied.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = original;
    }
  });

  const updateCartLine = async (key, quantity, control) => {
    control?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(routes.cartChange || `${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to update the cart.');
      await refreshCart();
      announce('Cart updated.');
    } catch (error) {
      announce(error.message);
    } finally {
      control?.removeAttribute('aria-busy');
    }
  };

  /* Drives the collapse/expand of the gift-message panel. Applied optimistically
     so the panel opens the instant the box is ticked, rather than after the cart
     round-trip; refreshCart() then re-renders the snippet with the authoritative
     class from Liquid, and the catch below reverts it if the update failed. */
  const syncGiftWrapOpen = (toggle) => {
    toggle.closest('[data-cart-gift-wrap]')?.classList.toggle('is-checked', toggle.checked);
  };

  const updateGiftWrap = async (toggle) => {
    const box = toggle.closest('[data-cart-gift-wrap]');
    const variantId = Number(box?.dataset.giftWrapVariantId);
    const lineKey = box?.dataset.giftWrapLineKey;
    if (!box || !variantId) return;
    syncGiftWrapOpen(toggle);
    toggle.disabled = true;
    box.setAttribute('aria-busy', 'true');
    try {
      const checked = toggle.checked;
      const message = box.querySelector('[data-cart-note]')?.value.trim() || '';
      const endpoint = checked
        ? (routes.cartAdd || `${window.Shopify?.routes?.root || '/'}cart/add.js`)
        : (routes.cartChange || `${window.Shopify?.routes?.root || '/'}cart/change.js`);
      const body = checked
        ? {
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _cart_gift_wrap: 'true',
                ...(message ? { 'Gift message': message } : {})
              }
            }]
          }
        : { id: lineKey || variantId, quantity: 0 };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update gift wrap.');
      await refreshCart();
      announce(checked ? 'Gift wrap added.' : 'Gift wrap removed.');
    } catch (error) {
      toggle.checked = !toggle.checked;
      syncGiftWrapOpen(toggle);
      announce(error.message);
    } finally {
      toggle.disabled = false;
      box.removeAttribute('aria-busy');
    }
  };

  const updateCartNote = async (note) => {
    try {
      const response = await fetch(routes.cartUpdate || `${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
      if (!response.ok) throw new Error('Unable to save the gift message.');
    } catch (error) {
      announce(error.message);
    }
  };

  customElements.define('product-form', ProductForm);
  customElements.define('variant-selects', VariantSelects);
  customElements.define('product-gallery', ProductGallery);
  customElements.define('predictive-search', PredictiveSearch);

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('product-form,variant-selects,product-gallery').forEach((element) => element.connectedCallback?.());
  });

  /* ── Site header + announcement bar: scroll-hide, scrolled state ──
     Offsets are measured in JS (not assumed via CSS sibling selectors,
     since Shopify wraps each section in its own div so .ann and
     .site-header are never true DOM siblings) and account for any
     third-party app bar (e.g. a "#qab_container" announcement app)
     that renders above our own chrome in normal document flow. ── */
  let headerScrollCleanup;
  const initHeaderScroll = () => {
    const header = document.getElementById('siteHeader');
    if (!header || header.dataset.scrollBound) return;
    headerScrollCleanup?.();
    header.dataset.scrollBound = 'true';
    /* The transparent, white-text header style only has enough contrast
       when it's sitting over a dark hero image (the homepage). Every other
       template has plain page content directly underneath, so the same
       "unscrolled" state at page load looked washed out there — and since
       it was purely scroll-position-driven, whether a given page happened
       to look washed-out or solid on load was really just whatever scroll
       position the browser restored, not a real per-template difference.
       Pages without a hero now always render the solid "scrolled" header. */
    const hasHero = header.dataset.hasHero === 'true';
    const scrollHideEnabled = header.dataset.scrollHide !== 'false';
    const solidOffset = Number(header.dataset.scrollSolidOffset) || 60;
    const hideOffset = Number(header.dataset.scrollHideOffset) || 120;
    const annBar = document.querySelector('.ann');
    let lastScrollY = window.scrollY;
    let annHeight = 0;
    let appBarHeight = 0;
    let observedAppBar = null;
    const resizeObserver = ('ResizeObserver' in window) ? new ResizeObserver(measure) : null;

    function applyOffset() {
      const y = window.scrollY;
      const appBarOffset = Math.max(0, appBarHeight - y);
      document.documentElement.style.setProperty('--qab-offset', `${appBarOffset}px`);
    }

    function measure() {
      const appBar = document.getElementById('qab_container');
      if (appBar && appBar !== observedAppBar) {
        resizeObserver?.observe(appBar);
        observedAppBar = appBar;
      }
      annHeight = annBar ? annBar.getBoundingClientRect().height : 0;
      appBarHeight = appBar ? appBar.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--ann-height', `${annHeight}px`);
      applyOffset();
    }

    const onScroll = () => {
      const y = window.scrollY;
      applyOffset();
      header.classList.toggle('scrolled', !hasHero || y > solidOffset);
      const shouldHide = scrollHideEnabled && y > hideOffset && y > lastScrollY;
      header.classList.toggle('hidden', shouldHide);
      annBar?.classList.toggle('hidden', shouldHide);
      lastScrollY = y;
    };

    measure();
    if (annBar) resizeObserver?.observe(annBar);
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Third-party app bars (announcement/promo apps) can inject or resize
       their content asynchronously after this script runs, and may not
       exist in the DOM yet when initHeaderScroll first executes — measure()
       re-queries for #qab_container every time so it's picked up whenever
       it actually appears. Poll briefly as a fallback in case it appears
       without ever triggering a resize/load event we're already watching. */
    const timers = [
      setTimeout(measure, 500),
      setTimeout(measure, 1500),
      setTimeout(measure, 3000)
    ];
    headerScrollCleanup = () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
      window.removeEventListener('scroll', onScroll);
      timers.forEach(clearTimeout);
    };
  };

  /* ── Announcement bar slider rotation ──
     Loops continuously and seamlessly: a clone of the first slide is
     appended after the real slides, so the slider can keep animating
     forward every time instead of snapping backward when it wraps from
     the last slide to the first (a plain `% total` reset would jump the
     transform value from e.g. -200% to -0%, which visually reverses
     direction for one tick). Once the clone finishes sliding into view
     (visually identical to the real first slide), the transform is reset
     to 0 with transitions disabled for that one frame, so the loop never
     visibly resets. */
  let annSliderCleanup;
  const initAnnSlider = () => {
    const slider = document.getElementById('annSlider');
    if (!slider || slider.dataset.bound) return;
    /* Each Theme Editor edit to this section re-renders #annSlider as a
       fresh DOM node, so the dataset.bound guard above only stops a given
       node from being bound twice — it doesn't stop the PREVIOUS node's
       still-running setInterval from ticking forever against a detached
       element. Clear it explicitly before binding the new one. */
    annSliderCleanup?.();
    slider.dataset.bound = 'true';
    const total = slider.children.length;
    if (total <= 1) return;
    const clone = slider.children[0].cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    slider.appendChild(clone);
    let current = 0;
    let resetTimer;
    const interval = setInterval(() => {
      current += 1;
      slider.style.transition = '';
      slider.style.transform = `translateX(-${current * 100}%)`;
      if (current === total) {
        resetTimer = setTimeout(() => {
          slider.style.transition = 'none';
          current = 0;
          slider.style.transform = 'translateX(0)';
          slider.offsetHeight; // eslint-disable-line no-unused-expressions -- force reflow so the next tick's transition re-enables cleanly
        }, 650);
      }
    }, 4000);
    annSliderCleanup = () => {
      clearInterval(interval);
      clearTimeout(resetTimer);
    };
  };

  initHeaderScroll();
  initAnnSlider();
  /* Reconciles coupon rows with what Shopify actually has on the cart. Safe to
     call anywhere: it exits immediately when no coupon rows are present. */
  syncCartCoupons();
  document.addEventListener('shopify:section:load', () => {
    initHeaderScroll();
    initAnnSlider();
    syncCartCoupons();
  });

  /* Mobile menu "Shop" accordion. Bound once at the document level rather
     than per-element, so it keeps working after the header section is
     re-rendered (Theme Editor reload, cart refresh) with no rebinding and
     no risk of duplicate listeners. aria-expanded is the single source of
     truth — the caret rotation is driven off it in CSS. */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-mobile-submenu-toggle]');
    if (!toggle) return;
    const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!submenu) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    submenu.hidden = expanded;
  });

  /* Free-sample urgency countdown — best-effort only, not a secure expiry.
     The window length is merchant-configurable (Theme Settings → Cart and
     search → "Free sample reservation window", default 15 min), emitted as
     window.themeSampleCountdownMinutes by sections/header.liquid. Once it
     elapses, the theme actually removes any free (₹0) line item from the
     cart via the Cart API. This is NOT airtight enforcement: it only runs
     while a shopper has the site open (checked on load and every tick), so
     closing the tab, switching devices, or a completely separate session
     can still let the sample slip through with no backend guarantee (see
     CLAUDE.md's "Free-sample flow" note — true enforcement needs a Shopify
     Function or backend app). Re-querying [data-sample-countdown] on every
     tick (rather than binding listeners once) means it automatically covers
     elements added later by a cart-drawer AJAX refresh or a Theme Editor
     section reload, with no rebinding logic needed. The start time persists
     across page loads within the same session (sessionStorage) so it
     doesn't look like it resets on every reload, and is cleared once the
     sample is actually removed so a later re-claim (see discovery-
     popup.liquid's claimSample()) starts a fresh window. */
  const SAMPLE_COUNTDOWN_MS = (Number(window.themeSampleCountdownMinutes) || 15) * 60 * 1000;
  const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };
  let removingExpiredSample = false;
  const removeExpiredSample = async () => {
    if (removingExpiredSample) return;
    removingExpiredSample = true;
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${rootUrl}cart.js`)).json();
      const freeLines = cart.items.filter((item) => item.final_price === 0);
      if (freeLines.length) {
        for (const line of freeLines) {
          await fetch(routes.cartChange || `${rootUrl}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
        sessionStorage.removeItem('chantero-sample-timer-start');
        await refreshCart();
      }
    } catch (error) {
      /* Best-effort only — a failed request just means removal retries on the next tick. */
    } finally {
      removingExpiredSample = false;
    }
  };
  const initSampleCountdown = () => {
    let start = Number(sessionStorage.getItem('chantero-sample-timer-start'));
    if (!start) {
      start = Date.now();
      sessionStorage.setItem('chantero-sample-timer-start', String(start));
    }
    const tick = () => {
      const elapsed = Date.now() - start;
      const banners = document.querySelectorAll('[data-sample-countdown]');
      if (elapsed >= SAMPLE_COUNTDOWN_MS) {
        if (banners.length) removeExpiredSample();
        return;
      }
      const text = formatCountdown(SAMPLE_COUNTDOWN_MS - elapsed);
      banners.forEach((el) => { el.textContent = text; });
    };
    tick();
    setInterval(tick, 1000);
  };
  initSampleCountdown();

  /* Per-section "Heading font" / "Body font" pickers (see
     snippets/font-value.liquid). Applied in JS rather than a CSS rule on
     purpose: a CSS selector specific enough to reliably beat an arbitrary
     section's own heading/paragraph classes (e.g. .pdp-title, .hero-h1)
     is, by definition, ALSO specific enough to beat them when no real font
     is chosen — silently overriding every section's intended heading font
     with the inherited body font even at the "Theme default" setting. Only
     touching the DOM when a real (non-"inherit") font was actually picked
     avoids that entirely: nothing is touched, nothing can change, unless a
     merchant explicitly selects a font. */
  const applyFontPickers = (root) => {
    root.querySelectorAll('[data-font-scope]').forEach((scope) => {
      const style = getComputedStyle(scope);
      const headingFont = style.getPropertyValue('--section-heading-font').trim();
      const bodyFont = style.getPropertyValue('--section-body-font').trim();
      if (headingFont && headingFont !== 'inherit') {
        scope.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((el) => { el.style.fontFamily = headingFont; });
      }
      if (bodyFont && bodyFont !== 'inherit') {
        scope.querySelectorAll('p').forEach((el) => { el.style.fontFamily = bodyFont; });
      }
    });
  };
  applyFontPickers(document);
  document.addEventListener('shopify:section:load', (event) => applyFontPickers(event.target));

  window.ChanteroTheme = { openDrawer, closeDrawer, announce, refreshCart };
  root.classList.add('theme-ready');
})();
